import { z } from "zod";
import { PublicIdSchema } from "./common";
import { UnavailableTimeWindow, UnavailableTimeWindowInput } from "./pickup-spot";

function coordinateString(label: string, min: number, max: number) {
  return z
    .string()
    .trim()
    .min(1)
    .refine(
      (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= min && parsed <= max;
      },
      { message: `${label}は${min}から${max}の数値で指定してください` },
    );
}

const LatitudeString = coordinateString("緯度", -90, 90);
const LongitudeString = coordinateString("経度", -180, 180);

export const DeliverySpot = z.object({
  id: PublicIdSchema,
  delivery_spot_code: z.string().nullable(),
  name: z.string(),
  address: z.string(),
  latitude: z.string().nullable(),
  longitude: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  remarks: z.string().nullable(),
  unavailable_times: z.array(UnavailableTimeWindow),
  person_name: z.string().nullable(),
  person_name_kana: z.string().nullable(),
  branch: z.string().nullable(),
  building: z.string().nullable(),
  handover_location: z.string().nullable(),
  default_duration_minutes: z.number().int().nullable(),
  caution_notes: z.string().nullable(),
  created_at: z.iso.datetime({ offset: true }),
  updated_at: z.iso.datetime({ offset: true }),
});

export const ListDeliverySpotsInput = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
  /** 名称・納品先コード・住所の部分一致 (3 文字以上は FTS5、未満は LIKE) */
  q: z.string().trim().max(200).optional(),
  // false で総件数 COUNT を省略 (コンボボックス等 total 不要の呼出向け)。
  // このとき pagination.total は「取得件数 (続きがあれば limit+1)」を返す。
  // z.coerce.boolean() は "false" を true 扱いするため preprocess で解釈する。
  with_total: z
    .preprocess((v) => (typeof v === "string" ? v === "true" : v), z.boolean())
    .default(true),
});

export const CreateDeliverySpotInput = z.object({
  delivery_spot_code: z.string().max(50).nullable().optional(),
  name: z.string().min(1).max(200),
  address: z.string().min(1).max(500),
  latitude: LatitudeString,
  longitude: LongitudeString,
  phone: z.string().max(20).nullable().optional(),
  email: z.string().max(255).nullable().optional(),
  remarks: z.string().nullable().optional(),
  unavailable_times: z.array(UnavailableTimeWindowInput).optional(),
  person_name: z.string().max(200).nullable().optional(),
  person_name_kana: z.string().max(200).nullable().optional(),
  branch: z.string().max(200).nullable().optional(),
  building: z.string().max(200).nullable().optional(),
  handover_location: z.string().max(500).nullable().optional(),
  default_duration_minutes: z.number().int().nullable().optional(),
  caution_notes: z.string().nullable().optional(),
});

export const UpdateDeliverySpotInput = z.object({
  delivery_spot_code: z.string().max(50).nullable().optional(),
  name: z.string().min(1).max(200),
  address: z.string().min(1).max(500),
  latitude: LatitudeString,
  longitude: LongitudeString,
  phone: z.string().max(20).nullable().optional(),
  email: z.string().max(255).nullable().optional(),
  remarks: z.string().nullable().optional(),
  unavailable_times: z.array(UnavailableTimeWindowInput).optional(),
  person_name: z.string().max(200).nullable().optional(),
  person_name_kana: z.string().max(200).nullable().optional(),
  branch: z.string().max(200).nullable().optional(),
  building: z.string().max(200).nullable().optional(),
  handover_location: z.string().max(500).nullable().optional(),
  default_duration_minutes: z.number().int().nullable().optional(),
  caution_notes: z.string().nullable().optional(),
});

export type DeliverySpotDto = z.infer<typeof DeliverySpot>;
export type CreateDeliverySpotInputDto = z.infer<typeof CreateDeliverySpotInput>;
export type UpdateDeliverySpotInputDto = z.infer<typeof UpdateDeliverySpotInput>;
export { UnavailableTimeWindow, UnavailableTimeWindowInput };
